import { useState } from 'react';
import { FiX, FiAlertTriangle, FiArrowRight, FiLayers } from 'react-icons/fi';
import { montantTtc } from '../utils/montantTtc';

const fmtAmt = (n) => Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

export default function CollisionResolverModal({ open, collisions, onClose, onConfirm }) {
  const [strategy, setStrategy] = useState('stagger');
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FiAlertTriangle className="h-4 w-4 text-orange-500" />
            {collisions.length} collision{collisions.length > 1 ? 's' : ''} RUM + date
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-[11px] text-gray-600">
            LCL refuse 2 prélèvements pour le même mandat à la même date. Choisissez comment résoudre :
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setStrategy('stagger')}
              className={`p-3 text-left rounded border-2 transition-colors ${
                strategy === 'stagger' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-800">
                <FiArrowRight className="h-3.5 w-3.5" />
                Étalement J+1 ouvré
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                1 facture = 1 prélèvement. Le 1er garde sa date, les suivants sont décalés au prochain jour ouvré.
              </div>
            </button>
            <button
              onClick={() => setStrategy('merge')}
              className={`p-3 text-left rounded border-2 transition-colors ${
                strategy === 'merge' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-800">
                <FiLayers className="h-3.5 w-3.5" />
                Fusion en 1 prélèvement
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                Les factures du groupe sont sommées en 1 ligne CSV (libellé concaténé). Lettrage compta manuel.
              </div>
            </button>
          </div>

          <div className="border border-gray-200 rounded max-h-64 overflow-y-auto">
            <table className="min-w-full text-[10px]">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-semibold text-gray-500">RUM</th>
                  <th className="px-2 py-1 text-left font-semibold text-gray-500">Date</th>
                  <th className="px-2 py-1 text-left font-semibold text-gray-500">Factures</th>
                  <th className="px-2 py-1 text-right font-semibold text-gray-500">Total TTC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {collisions.map((c, i) => {
                  const total = c.items.reduce((s, x) => s + montantTtc(x.montant_facture), 0);
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1 font-mono text-gray-700">
                        {c.rum.length > 18 ? `${c.rum.slice(0, 18)}…` : c.rum}
                      </td>
                      <td className="px-2 py-1 text-gray-700">{fmtDate(c.date)}</td>
                      <td className="px-2 py-1 text-gray-700">
                        {c.items.map((x) => `${x.numero_facture} (${x.exercice_annee})`).join(', ')}
                      </td>
                      <td className="px-2 py-1 text-right font-semibold text-gray-800">{fmtAmt(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] rounded border border-gray-300 hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={() => onConfirm(strategy)}
            className="px-3 py-1.5 text-[11px] font-semibold rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Continuer avec {strategy === 'stagger' ? 'Étalement' : 'Fusion'}
          </button>
        </div>
      </div>
    </div>
  );
}
